import { useState, useMemo } from "react";

// Walkthrough of the app as it actually works today. Steps marked adminOnly
// are dropped for staff accounts, so nobody is shown a door the server won't
// open for them.
const STEPS = [
  {
    icon: "👋",
    title: "Welcome to KAC Tennis Programming",
    body: "This is where the club's clinics get built: members go into groups, groups go onto courts, and the schedule goes out by email. This walkthrough covers what's here — reopen it any time from Tutorial at the bottom of the left menu.",
  },
  {
    icon: "🧭",
    title: "Finding your way",
    body: "Everything lives in the menu down the left. Schedule shows the week as a List or a Grid, Members holds the roster, sign-ups and coaches, and Planning helps you find a slot or a person. Stats, Issues and Email sit below those. On a narrow screen the menu hides behind the ☰ button.",
  },
  {
    icon: "➕",
    title: "Building groups",
    body: "In Schedule → List, click \"+ New\" beside a tier to create a group. In Schedule → Grid you can click any empty cell to create one already filled in with that day, time and court. Give it a coach and a court, and it appears on the week straight away.",
  },
  {
    icon: "🖱️",
    title: "Placing members",
    body: "Drag a member's card onto a group to move them. Or click the card to open them, scroll to \"Assign to Group\" and pick from the list. The same card is where you edit their email, tier, availability and household.",
  },
  {
    icon: "⚠️",
    title: "When something doesn't fit",
    body: "An amber ⚠ means the member isn't free at their group's time. A purple 👥 means a preferred partner ended up elsewhere. Neither blocks you — they're prompts. The Issues view collects everything needing a decision, including courts double-booked, and its badge counts what's outstanding.",
  },
  {
    icon: "🏷️",
    title: "Follow-up tags",
    body: "Talking to someone who wants another session, or a private? Open their card and tag them under Follow-up. Tagged members are then filterable on the Members roster, so you can come back and place them when there's time. Tags stay until you remove them.",
  },
  {
    icon: "📝",
    title: "Notes",
    body: "Both members and groups take notes. A member's note saves as soon as you click away — no Edit/Save round trip — so jotting down what someone said takes a moment. A 📝 on a group card means there's something written there.",
  },
  {
    icon: "🔍",
    title: "Filtering the week",
    body: "The Grid can be narrowed to one level — pick a tier from \"All levels\" above the grid to see only Junior Intermediate, say, and everything else fades back. Handy when you're balancing one level across the week. Grid search also finds which session a given member is in.",
  },
  {
    icon: "📨",
    title: "Sign-ups and email",
    body: "New members apply through the public form; those land in Members → Sign-ups with a badge, and approving one adds them to the roster unassigned. Copy the sign-up link from that same screen. When the schedule is ready, the Email view sends it out or exports it as CSV.",
  },
  {
    icon: "🗓️",
    title: "Terms",
    body: "The Terms button in the top bar saves the current schedule as a season and switches between them. Starting a new term snapshots what's there first, so last season stays intact and you can look back at it.",
  },
  {
    icon: "🔐",
    title: "Your account",
    body: "You're signed in as yourself and you stay signed in — the app won't log you out between visits. Your name, Change password and Sign out are at the bottom of the left menu. Changing your password signs out every other browser you're signed in on, which is the quickest fix if you ever leave yourself logged in somewhere.",
  },
  {
    icon: "👥",
    title: "Approving accounts",
    adminOnly: true,
    body: "Anyone can request an account from the sign-in screen, but a request grants nothing on its own. Admin → Accounts is where you activate, decline, suspend or reinstate them, and where you make someone else an admin. The badge shows requests waiting.",
  },
  {
    icon: "🗒️",
    title: "The activity log",
    adminOnly: true,
    body: "Admin → Activity records every change with who made it and what the value was before. It's written by the server as edits are saved, so it doesn't depend on anyone remembering to log anything. Filter by person, and expand a row to see the exact fields that moved.",
  },
  {
    icon: "✅",
    title: "That's the tour",
    body: "You know enough to work. Tutorial at the bottom of the menu brings this back whenever you want it.",
  },
];

// Bumped when the content changes materially — not used to force a re-show
// today, but it's what a "show me what's new" prompt would key off.
export const TUTORIAL_VERSION = 2;

export default function TutorialModal({ onClose, isAdmin = false }) {
  const steps = useMemo(() => STEPS.filter(s => !s.adminOnly || isAdmin), [isAdmin]);
  const [step, setStep] = useState(0);
  const safeStep = Math.min(step, steps.length - 1);
  const isFirst = safeStep === 0;
  const isLast = safeStep === steps.length - 1;
  const current = steps[safeStep];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 max-h-[90vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-4 overflow-y-auto">
          <div className="flex items-start justify-between">
            <div className="text-3xl">{current.icon}</div>
            <button onClick={onClose} className="text-xl leading-none text-gray-400 hover:text-gray-700 transition-colors">×</button>
          </div>
          <h2 className="text-lg font-bold text-gray-800 mt-2">{current.title}</h2>
          <p className="text-sm text-gray-600 mt-2 leading-relaxed">{current.body}</p>
        </div>

        <div className="px-5 pb-3 flex items-center justify-center gap-1.5 flex-wrap">
          {steps.map((_, i) => (
            <button
              key={i}
              onClick={() => setStep(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === safeStep ? "w-5 bg-blue-500" : "w-1.5 bg-gray-200 hover:bg-gray-300"
              }`}
              aria-label={`Go to step ${i + 1}`}
            />
          ))}
        </div>

        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
          <button
            onClick={onClose}
            className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
          >
            Skip
          </button>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400 tabular-nums">
              {safeStep + 1} / {steps.length}
            </span>
            <div className="flex gap-2">
              {!isFirst && (
                <button
                  onClick={() => setStep(s => s - 1)}
                  className="border border-gray-300 hover:bg-gray-50 text-gray-600 text-sm font-medium px-4 py-1.5 rounded transition-colors"
                >
                  Back
                </button>
              )}
              <button
                onClick={() => (isLast ? onClose() : setStep(s => s + 1))}
                className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-1.5 rounded transition-colors"
              >
                {isLast ? "Done" : "Next"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
